import * as moment_ from 'moment';
const moment = moment_;

import { TitleCasePipe } from '@angular/common';

export const ItemTypes = [ 'unknown category', 'shipment', 'template', 'report', 'address book', 'hbl', 'label', 'attachment' ] as const;
export type ItemTypes = typeof ItemTypes[number];

export const ItemActivities = [ 'unknown activity', 'created', 'api_created', 'deleted', 'updated', 'unshared', 'printed' ] as const;
export type ItemActivities = typeof ItemActivities[number];

export const ItemActions = [ 'view', 'recover', 'run' ] as const;
export type ItemActions = typeof ItemActions[number];

export interface Action {
   type: ItemActions;
   icon: string;
   route: string;
}

export interface Activity {
   id: number;
   pkey: string;

   userFullName: string;
   name: string;

   actionExpiration: moment.Moment;
   isExpired: boolean;
   timeTillExpired: string;

   type: ItemTypes;
   activity: ItemActivities;
   activityText: string;
   actions: ItemActions[];

   timestamp: moment.Moment;
   date: string;
   time: string;

   isLast: boolean;
}

export class Activity implements Activity {
   constructor( act?: any ) {
      this.id = act?.id ?? 0;
      this.userFullName = act?.userFullName ?? '';
      this.name = act?.name ?? '';
      this.pkey = act?.pkey ?? '';

      [ this.actionExpiration, this.isExpired, this.timeTillExpired ] = ActivityExpirationMap( act?.actionExpiration );

      this.isLast = act?.isLast ?? true;

      [ this.timestamp, this.date, this.time ] = ActivityTimestamp( act?.timestamp );

      this.type = ActivityTypeMap( act?.type );
      [ this.activity, this.activityText ] = ActivityActivityMap( act?.activity, this.type );
      this.actions = ActivityActionsMap( act?.actions, this.type, this.activity );
   }
}

// supporting functions
function ActivityExpirationMap( actionExpiration: moment.Moment | string = moment() ): [ moment.Moment, boolean, string ] {
   const expiration = moment( actionExpiration );
   const isExpired = expiration.isSameOrBefore();
   const timeTillExpired = expiration.isAfter() ? expiration.fromNow( true ) : '';

   return [ expiration, isExpired, timeTillExpired ];
}

function ActivityTypeMap( type: string ): ItemTypes {
   const itemType = type.toLowerCase() as ItemTypes;

   return ItemTypes.includes( itemType ) ? itemType : ItemTypes[0];
}

function ActivityActivityMap( act: string, type: ItemTypes = ItemTypes[0] ): [ ItemActivities, string ] {
   const activity = ItemActivitiesMap( act.toLowerCase() );
   const text = ActivityTextMap( activity, type );

   return [ activity, text ];
}

function ActivityActionsMap( actions: string[] = [], type?: ItemTypes, activity?: ItemActivities ): ItemActions[] {
   return actions.map( a => a.toLowerCase() )
      .filter( action => { // make sure action makes sense in context
         switch ( action ) {
            case 'view':
               return [ 'created', 'api_created', 'printed' ].includes( activity );
            case 'recover':
               return [ 'updated', 'deleted', 'unshared' ].includes( activity );
            case 'run':
               return ( type === 'report' );
            default:
               return false;
         }
      }) as ItemActions[];
}

function ActivityTimestamp( timestamp: moment.Moment | string = moment() ): [ moment.Moment, string, string ] {
   const datetime = moment( timestamp );

   return [ datetime, datetime.format( 'MMMM D, YYYY' ), datetime.format( 'HH:mm' ) ];
}

function ItemActivitiesMap( activity ): ItemActivities {
   switch ( activity ) {
      case 'create':
      case 'created':
         return 'created';
      case 'api_create':
      case 'api_created':
         return 'api_created';
      case 'delete':
      case 'deleted':
         return 'deleted';
      case 'update':
      case 'updated':
         return 'updated';
      case 'unshare':
      case 'unshared':
         return 'unshared';
      case 'print':
      case 'printed':
         return 'printed';
      default:
         return 'unknown activity';
   }
}

function ActivityTextMap( activity: ItemActivities, type: ItemTypes ) {
   let [ actText, typeText, suffix ] = [ '', '', '' ];

   switch ( type ) {
      case 'hbl':
         typeText = 'HBL';
         break;
      case 'address book':
         typeText = 'Address Book Location';
         break;
      default:
         typeText = new TitleCasePipe().transform( type );
   }
   switch ( activity ) {
      case 'api_created':
         actText = 'Created';
         suffix = 'via API';
         break;
      default:
         actText = new TitleCasePipe().transform( activity );
   }

   return `${actText} ${typeText} ${suffix}`;
}
